import type { EnvVar } from "../../../utils/types";
import React, { useCallback, useContext } from "react";
import { arrayUnion, doc, updateDoc } from "@firebase/firestore";
import { customFirestore } from "../../../firebase";
import { companyDataAtom } from "../../test-run/test-run-atoms";
import { trackSentryException } from "../../../utils/helpers";
import { auditMeta } from "../../../utils/auditMeta";
import Button from "@mui/material/Button";
import { Form, Formik } from "formik";
import FormikTextInput from "../../../formikComponents/formik-text-input";
import FormikCheckbox from "../../../formikComponents/formik-checkbox";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AuthContext } from "../../../auth/AuthContext";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";
import { atomWithReset } from "jotai/utils";
import { Close, Save } from "@mui/icons-material";
import { useAtom, useSetAtom } from "jotai";
import { customAlertStateAtom } from "../../../components/custom-alert";

const AddEnvVarDialog = () => {
  const queryClient = useQueryClient();
  const { user } = useContext<any>(AuthContext);

  const [dialogState, setDialogState] = useAtom(addEnvVarDialogStateAtom);

  const setCustomAlertState = useSetAtom(customAlertStateAtom);

  const handleClose = useCallback(
    (event: {}, reason: string) => {
      if (reason && (reason === "backdropClick" || reason === "escapeKeyDown"))
        return;
      setDialogState((prev) => ({
        ...prev,
        open: false,
      }));
    },
    [setDialogState]
  );

  const saveEnvVarMutation = useMutation({
    mutationKey: ["saveEnvVar"],
    mutationFn: async (envVar: EnvVar) => {
      try {
        console.log("Saving", envVar);

        const settingsDoc = doc(customFirestore, "settings", user.companyId);
        // Stamp the trusted actor on the settings doc so the audit trigger can
        // attribute this env-var change (rules reject a forged updatedBy).
        await updateDoc(settingsDoc, {
          envVars: arrayUnion(envVar),
          ...auditMeta(),
        });

        await queryClient.invalidateQueries({
          queryKey: ["getEnvVars", user.companyId],
        });
        companyDataAtom.remove({ organisationId: user.companyId });
        queryClient.removeQueries({
          queryKey: ["getCompanyData"],
        });
        setCustomAlertState({
          open: true,
          type: "success",
          title: `Env var saved successfully`,
        });
      } catch (e) {
        trackSentryException(e);
        setCustomAlertState({
          open: true,
          type: "error",
          title: "Something went wrong",
          description: "An error occurred during when adding the env var",
        });
      }
      handleClose({}, "closeIconButtonClose");
    },
  });

  return (
    <Dialog
      maxWidth={"sm"}
      fullWidth={true}
      open={dialogState.open}
      onClose={handleClose}
    >
      <Formik
        initialValues={{
          envVarKey: "",
          envVarValue: "",
          envVarIsSecret: false,
        }}
        validate={(values) => {
          const errors: { envVarKey?: string; envVarValue?: string } = {};
          if (!values.envVarKey) {
            errors.envVarKey = "Required";
          } else if (!values.envVarValue) {
            errors.envVarValue = "Required";
          } else if (
            dialogState.envVars
              .map((saved) => saved.key)
              .includes(values.envVarKey)
          ) {
            errors.envVarKey = "Env Var with this key already exists";
          }
          return errors;
        }}
        onSubmit={async (values) => {
          await saveEnvVarMutation.mutateAsync({
            key: values.envVarKey,
            value: values.envVarValue,
            lastEditTimestamp: Math.floor(Date.now() / 1000),
            isSecret: values.envVarIsSecret,
          });
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <DialogTitle
              id="alert-dialog-title"
              sx={{ paddingRight: "8px", paddingTop: "10px" }}
            >
              <Box display="flex" alignItems="top">
                <Box flexGrow={1} paddingRight={"6px"} paddingTop={"4px"}>
                  Enter new value
                </Box>
                <Box>
                  <Tooltip title={"Close"}>
                    <span>
                      <IconButton
                        disabled={saveEnvVarMutation.isPending}
                        onClick={() => {
                          handleClose({}, "closeIconButtonClose");
                        }}
                      >
                        <Close />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers className={"flex flex-col gap-3.5"}>
              <div className={"flex gap-3"}>
                <FormikTextInput
                  name={`envVarKey`}
                  label={"Key"}
                  placeholder={"Key"}
                  type={"text"}
                  required
                />
                <FormikTextInput
                  name={`envVarValue`}
                  label={"Value"}
                  placeholder={"Value"}
                  type={"text"}
                  required
                />
              </div>
              <div className={"pl-0.5 pt-2"}>
                <FormikCheckbox
                  name={`envVarIsSecret`}
                  label={"Secure Entry: Omit Value from Interfaces and Logs"}
                  type={"checkbox"}
                />
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                loading={isSubmitting}
                type={"submit"}
                loadingPosition="start"
                startIcon={<Save />}
                variant="contained"
              >
                Save
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

const addEnvVarDialogStateAtom = atomWithReset<{
  open: boolean;
  envVars: EnvVar[];
}>({ open: false, envVars: [] });

export default AddEnvVarDialog;
export { addEnvVarDialogStateAtom };
